import { useState } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Route, Switch, useLocation } from "wouter";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2 } from "lucide-react";
import { AuthContext, useAuthState, useAuth } from "@/hooks/use-auth";
import Sidebar from "@/components/Sidebar";
import HomePage from "./pages/HomePage";
import UploadPage from "./pages/UploadPage";
import EntitiesPage from "./pages/EntitiesPage";
import AddEntityPage from "./pages/AddEntityPage";
import StatsPage from "./pages/StatsPage";
import EntityDetailPage from "./pages/EntityDetailPage";
import ApiDocsPage from "./pages/ApiDocsPage";
import SicarPage from "./pages/SicarPage";
import AgrofitPage from "./pages/AgrofitPage";
import SicarWorkerPage from "./pages/SicarWorkerPage";
import CprmPage from "./pages/CprmPage";
import FunaiPage from "./pages/FunaiPage";
import SgbPage from "./pages/SgbPage";
import AnmPage from "./pages/AnmPage";
import IbamaPage from "./pages/IbamaPage";
import IcmbioPage from "./pages/IcmbioPage";
import PgfnPage from "./pages/PgfnPage";
import RotinasPage from "./pages/RotinasPage";
import Cardmv0Page from "./pages/Cardmv0Page";
import FarmWayPage from "./pages/FarmWayPage";
import NotFound from "./pages/not-found";

function LoginPage() {
  const { login } = useAuth();
  const [, setLocation] = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || "Erro ao fazer login");
        return;
      }
      login(data.token, data.user);
      setLocation("/");
    } catch {
      setError("Erro de conexao com o servidor");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center" data-testid="page-login">
      <Card className="w-full max-w-md" data-testid="card-login">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold text-primary">MAVRION HUB API</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">Faca login para acessar o sistema</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="username">Usuario</Label>
              <Input
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Seu usuario"
                data-testid="input-login-username"
                autoComplete="username"
              />
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Sua senha"
                data-testid="input-login-password"
                autoComplete="current-password"
              />
            </div>
            {error && <p className="text-destructive text-sm" data-testid="text-login-error">{error}</p>}
            <Button type="submit" className="w-full" disabled={loading || !username || !password} data-testid="button-login">
              {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Entrar
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

function AppContent() {
  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar />
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Switch>
            <Route path="/" component={HomePage} />
            <Route path="/upload" component={UploadPage} />
            <Route path="/entities/:id" component={EntityDetailPage} />
            <Route path="/entities" component={EntitiesPage} />
            <Route path="/add" component={AddEntityPage} />
            <Route path="/stats" component={StatsPage} />
            <Route path="/docs" component={ApiDocsPage} />
            <Route path="/sicar" component={SicarPage} />
            <Route path="/agrofit" component={AgrofitPage} />
            <Route path="/sicar-worker" component={SicarWorkerPage} />
            <Route path="/cprm" component={CprmPage} />
            <Route path="/funai" component={FunaiPage} />
            <Route path="/sgb" component={SgbPage} />
            <Route path="/anm" component={AnmPage} />
            <Route path="/ibama" component={IbamaPage} />
            <Route path="/icmbio" component={IcmbioPage} />
            <Route path="/pgfn" component={PgfnPage} />
            <Route path="/rotinas" component={RotinasPage} />
            <Route path="/cardmv-0" component={Cardmv0Page} />
            <Route path="/farmway" component={FarmWayPage} />
            <Route component={NotFound} />
          </Switch>
        </div>
      </main>
      <Toaster />
    </div>
  );
}

function App() {
  const auth = useAuthState();

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthContext.Provider value={auth}>
          {auth.isAuthenticated ? <AppContent /> : <LoginPage />}
        </AuthContext.Provider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
